import { SxProps, Theme } from '@mui/material';
import { colors } from '@/theme/colors';
import { textStyles } from '@/theme/typography';

export const appBar = (trigger: boolean): SxProps<Theme> => ({
  bgcolor: trigger ? 'rgba(255, 255, 255, 0.97)' : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(16px)',
  boxShadow: trigger ? '0 1px 24px rgba(0,0,0,0.06)' : 'none',
  borderBottom: trigger ? '1px solid rgba(0,0,0,0.04)' : '1px solid transparent',
  py: 0.5,
  transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
  zIndex: 1200,
});

export const toolbar: SxProps<Theme> = {
  maxWidth: 1400,
  width: '100%',
  mx: 'auto',
  px: { xs: 2, md: 4 },
  minHeight: { xs: 64, md: 70 },
};

export const logoWrapper: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  mr: { xs: 1, md: 4 },
};

export const logo: SxProps<Theme> = {
  height: { xs: 28, sm: 32, md: 40 },
  width: 'auto',
};

export const desktopNav: SxProps<Theme> = {
  display: { xs: 'none', lg: 'flex' },
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  flex: 1,
};

export const navButton = (active: boolean): SxProps<Theme> => ({
  color: active ? colors.primary.main : '#3a3a4a',
  fontSize: '0.9rem',
  fontWeight: active ? 600 : 400,
  textTransform: 'none',
  px: 1.5,
  py: 0.75,
  borderRadius: '8px',
  position: 'relative',
  transition: 'all 0.25s ease',
  '&:hover': {
    bgcolor: 'rgba(81, 60, 241, 0.05)',
    color: colors.primary.main,
  },
  '&::after': active
    ? {
        content: '""',
        position: 'absolute',
        bottom: 2,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 20,
        height: 2,
        borderRadius: 1,
        bgcolor: colors.primary.main,
      }
    : {},
});

export const bookDemoButton: SxProps<Theme> = {
  bgcolor: '#0f0f1a',
  color: colors.common.white,
  borderRadius: '8px',
  px: 3,
  py: 1,
  ...textStyles.labelLarge,
  textTransform: 'none',
  boxShadow: '0 2px 12px rgba(15,15,26,0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    bgcolor: colors.text.primary,
    boxShadow: '0 6px 20px rgba(15,15,26,0.25)',
    transform: 'translateY(-1px)',
  },
};

export const getStartedButton: SxProps<Theme> = {
  bgcolor: colors.primary.main,
  color: colors.common.white,
  borderRadius: '8px',
  px: 3,
  py: 1,
  ...textStyles.labelLarge,
  textTransform: 'none',
  boxShadow: '0 2px 12px rgba(81, 60, 241, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    bgcolor: '#6352f5',
    boxShadow: '0 6px 20px rgba(81, 60, 241, 0.3)',
    transform: 'translateY(-1px)',
  },
};

export const mobileMenuButton: SxProps<Theme> = {
  display: { lg: 'none' },
  ml: 'auto',
  color: '#3a3a4a',
};

export const mobileDrawerPaper: SxProps<Theme> = {
  width: 300,
  borderRadius: '16px 0 0 16px',
  display: 'flex',
  flexDirection: 'column',
};

export const mobileDrawerHeader: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2.5,
  py: 2,
};

export const mobileCloseButton: SxProps<Theme> = {
  bgcolor: 'rgba(0,0,0,0.04)',
  borderRadius: '10px',
  width: 36,
  height: 36,
  '&:hover': {
    bgcolor: 'rgba(0,0,0,0.08)',
  },
};

export const mobileNavItem = (active: boolean): SxProps<Theme> => ({
  borderRadius: '12px',
  mx: 1,
  mb: 0.5,
  py: 1.2,
  px: 1.5,
  bgcolor: active ? 'rgba(81, 60, 241, 0.06)' : 'transparent',
  color: active ? colors.primary.main : colors.text.primary,
  transition: 'all 0.2s ease',
  '&:hover': {
    bgcolor: active ? 'rgba(81, 60, 241, 0.08)' : 'rgba(0,0,0,0.03)',
  },
});

export const mobileNavIcon = (active: boolean): SxProps<Theme> => ({
  minWidth: 40,
  color: active ? colors.primary.main : colors.text.secondary,
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  },
});

export const mobileBookDemo: SxProps<Theme> = {
  bgcolor: '#0f0f1a',
  borderRadius: '12px',
  py: 1.4,
  textTransform: 'none',
  fontSize: '0.938rem',
  fontWeight: 600,
  boxShadow: '0 2px 12px rgba(15,15,26,0.15)',
  '&:hover': {
    bgcolor: colors.text.primary,
    boxShadow: '0 4px 16px rgba(15,15,26,0.2)',
  },
};

export const mobileGetStartedBtn: SxProps<Theme> = {
  bgcolor: colors.primary.main,
  borderRadius: '12px',
  py: 1.4,
  textTransform: 'none',
  fontSize: '0.938rem',
  fontWeight: 600,
  boxShadow: '0 2px 12px rgba(81, 60, 241, 0.2)',
  '&:hover': {
    bgcolor: '#6352f5',
    boxShadow: '0 4px 16px rgba(81, 60, 241, 0.3)',
  },
};

export const spacer: SxProps<Theme> = {
  minHeight: { xs: 64, md: 70 },
};
